import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  AlertCircle,
  Star,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui';
import { Skeleton } from '@/components/ui/Skeleton';
import { Modal } from '@/components/ui/Modal';
import { patientScoreService } from '@/services/patientScoreService';
import type {
  PatientScoreDetail,
  ScoreClassification,
  ScoreTrend,
} from '@/types/patientScore';
import styles from './PatientScoresPage.module.css';

const PAGE_SIZE = 20;

const CLASSIFICATION_CONFIG: Record<
  ScoreClassification,
  { label: string; color: string; bgColor: string }
> = {
  GOLD: { label: 'Ouro', color: '#856404', bgColor: '#FFF3CD' },
  SILVER: { label: 'Prata', color: '#495057', bgColor: '#E9ECEF' },
  BRONZE: { label: 'Bronze', color: '#7B4F2A', bgColor: '#FDEBD0' },
  AT_RISK: { label: 'Em Risco', color: '#721C24', bgColor: '#F8D7DA' },
  INACTIVE: { label: 'Inativo', color: '#383D41', bgColor: '#D6D8DB' },
};

const CLASSIFICATION_DOT_COLORS: Record<ScoreClassification, string> = {
  GOLD: '#FFD700',
  SILVER: '#C0C0C0',
  BRONZE: '#CD7F32',
  AT_RISK: '#FF6B6B',
  INACTIVE: '#6C757D',
};

function getScoreColor(score: number): string {
  if (score >= 85) return '#FFD700';
  if (score >= 65) return '#C0C0C0';
  if (score >= 40) return '#CD7F32';
  if (score >= 20) return '#FF6B6B';
  return '#6C757D';
}

function TrendIcon({ trend }: { trend: ScoreTrend }) {
  switch (trend) {
    case 'UP':
      return <TrendingUp size={16} className={styles.trendUp} aria-label="Tendência positiva" />;
    case 'DOWN':
      return <TrendingDown size={16} className={styles.trendDown} aria-label="Tendência negativa" />;
    default:
      return <Minus size={16} className={styles.trendStable} aria-label="Estável" />;
  }
}

function formatRecency(lastDate: string | null): string {
  if (!lastDate) return 'Nunca';
  const diff = Math.floor(
    (Date.now() - new Date(lastDate).getTime()) / (1000 * 60 * 60 * 24),
  );
  if (diff === 0) return 'Hoje';
  if (diff === 1) return '1 dia';
  if (diff < 30) return `${diff} dias`;
  if (diff < 60) return '1 mês';
  return `${Math.floor(diff / 30)} meses`;
}

/**
 * Dedicated patient scores page with distribution cards,
 * sortable table, and detail modal.
 *
 * Route: /admin/patient-scores
 */
export function PatientScoresPage() {
  const [page, setPage] = useState(1);
  const [classificationFilter, setClassificationFilter] = useState<string | undefined>();
  const [sortBy, setSortBy] = useState<string>('SCORE');
  const [selectedPatient, setSelectedPatient] = useState<PatientScoreDetail | null>(null);

  const {
    data: distribution,
    isLoading: isDistLoading,
  } = useQuery({
    queryKey: ['patient-score-distribution'],
    queryFn: () => patientScoreService.getDistribution(),
  });

  const {
    data: scoresData,
    isLoading: isScoresLoading,
    error: scoresError,
  } = useQuery({
    queryKey: ['patient-scores', page, classificationFilter, sortBy],
    queryFn: () =>
      patientScoreService.getScores(page, PAGE_SIZE, classificationFilter, sortBy),
  });

  const handleClassificationClick = useCallback(
    (classification: ScoreClassification) => {
      setClassificationFilter((prev) =>
        prev === classification ? undefined : classification,
      );
      setPage(1);
    },
    [],
  );

  const handleSort = useCallback((column: string) => {
    setSortBy((prev) => (prev === column ? `${column}_ASC` : column));
    setPage(1);
  }, []);

  if (scoresError) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState} role="alert">
          <AlertCircle size={32} aria-hidden="true" />
          <p>Erro ao carregar scores dos pacientes.</p>
        </div>
      </div>
    );
  }

  const scores = scoresData?.items ?? [];
  const totalPages = scoresData?.totalPages ?? 0;

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>
            <Star size={24} aria-hidden="true" /> Score dos Pacientes
          </h1>
          <p className={styles.subtitle}>
            Engajamento e fidelidade dos pacientes baseado em 6 critérios
          </p>
        </div>
      </header>

      {/* Distribution cards */}
      <section aria-label="Distribuição por classificação" className={styles.distributionGrid}>
        {isDistLoading ? (
          <>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} variant="card" height="70px" />
            ))}
          </>
        ) : (
          <>
            {(
              [
                ['GOLD', distribution?.gold ?? 0],
                ['SILVER', distribution?.silver ?? 0],
                ['BRONZE', distribution?.bronze ?? 0],
                ['AT_RISK', distribution?.atRisk ?? 0],
                ['INACTIVE', distribution?.inactive ?? 0],
              ] as [ScoreClassification, number][]
            ).map(([key, count]) => (
              <button
                key={key}
                type="button"
                className={`${styles.distributionCard} ${
                  classificationFilter === key ? styles.distributionCardActive : ''
                }`}
                onClick={() => handleClassificationClick(key)}
                aria-pressed={classificationFilter === key}
              >
                <div
                  className={styles.distributionDot}
                  style={{ backgroundColor: CLASSIFICATION_DOT_COLORS[key] }}
                />
                <div className={styles.distributionInfo}>
                  <div className={styles.distributionCount}>{count}</div>
                  <div className={styles.distributionLabel}>
                    {CLASSIFICATION_CONFIG[key].label}
                  </div>
                </div>
              </button>
            ))}
          </>
        )}
      </section>

      {/* Active filter indicator */}
      {classificationFilter && (
        <div className={styles.controls}>
          <span
            className={styles.scoreBadge}
            style={{
              color: CLASSIFICATION_CONFIG[classificationFilter as ScoreClassification].color,
              backgroundColor: CLASSIFICATION_CONFIG[classificationFilter as ScoreClassification].bgColor,
            }}
          >
            Filtro: {CLASSIFICATION_CONFIG[classificationFilter as ScoreClassification].label}
            <button
              type="button"
              onClick={() => { setClassificationFilter(undefined); setPage(1); }}
              aria-label="Remover filtro"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}
            >
              <X size={12} />
            </button>
          </span>
        </div>
      )}

      {/* Table */}
      <div className={styles.tableWrapper}>
        {isScoresLoading ? (
          <div style={{ padding: 'var(--spacing-md)' }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} variant="rectangle" height="48px" />
            ))}
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th onClick={() => handleSort('NAME')}>Paciente</th>
                <th onClick={() => handleSort('SCORE')} style={{ width: '200px' }}>
                  Score
                </th>
                <th style={{ width: '100px' }}>Classificação</th>
                <th style={{ width: '60px' }}>Tend.</th>
                <th onClick={() => handleSort('RECENCY')} style={{ width: '100px' }}>
                  Última visita
                </th>
                <th style={{ width: '80px' }}>Consultas</th>
              </tr>
            </thead>
            <tbody>
              {scores.map((patient) => (
                <tr
                  key={patient.patientId}
                  onClick={() => setSelectedPatient(patient)}
                >
                  <td>
                    <strong>{patient.patientName}</strong>
                    <br />
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                      {patient.phone}
                    </span>
                  </td>
                  <td>
                    <div className={styles.scoreBar}>
                      <span
                        className={styles.scoreValue}
                        style={{ color: getScoreColor(patient.totalScore) }}
                      >
                        {patient.totalScore}
                      </span>
                      <div className={styles.scoreTrack}>
                        <div
                          className={styles.scoreFill}
                          style={{
                            width: `${patient.totalScore}%`,
                            backgroundColor: getScoreColor(patient.totalScore),
                          }}
                        />
                      </div>
                    </div>
                  </td>
                  <td>
                    <span
                      className={styles.scoreBadge}
                      style={{
                        color: CLASSIFICATION_CONFIG[patient.classification].color,
                        backgroundColor: CLASSIFICATION_CONFIG[patient.classification].bgColor,
                      }}
                    >
                      {CLASSIFICATION_CONFIG[patient.classification].label}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <TrendIcon trend={patient.trend} />
                  </td>
                  <td>{formatRecency(patient.lastAppointmentDate)}</td>
                  <td style={{ textAlign: 'center' }}>{patient.completedAppointments}</td>
                </tr>
              ))}
              {scores.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: 'var(--spacing-xl)', color: 'var(--color-text-secondary)' }}>
                    Nenhum paciente encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            <ChevronLeft size={16} />
          </Button>
          <span className={styles.paginationInfo}>
            {page} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      )}

      {/* Detail modal */}
      {selectedPatient && (
        <Modal
          title={`Score - ${selectedPatient.patientName}`}
          isOpen={!!selectedPatient}
          onClose={() => setSelectedPatient(null)}
        >
          <ScoreDetailContent patient={selectedPatient} />
        </Modal>
      )}
    </div>
  );
}

function ScoreDetailContent({ patient }: { patient: PatientScoreDetail }) {
  const breakdown = [
    { label: 'Presença', value: patient.attendanceScore, max: 30 },
    { label: 'Frequência', value: patient.frequencyScore, max: 25 },
    { label: 'Recência', value: patient.recencyScore, max: 20 },
    { label: 'Fidelidade', value: patient.loyaltyScore, max: 10 },
    { label: 'Confirmação', value: patient.confirmationScore, max: 10 },
    { label: 'Campanhas', value: patient.campaignScore, max: 5 },
  ];

  return (
    <div className={styles.scoreDetail}>
      <div className={styles.scoreDetailHeader}>
        <div
          className={styles.scoreDetailTotal}
          style={{ color: getScoreColor(patient.totalScore) }}
        >
          {patient.totalScore}
        </div>
        <span
          className={styles.scoreDetailClassification}
          style={{
            color: CLASSIFICATION_CONFIG[patient.classification].color,
          }}
        >
          {CLASSIFICATION_CONFIG[patient.classification].label}
          {' '}
          <TrendIcon trend={patient.trend} />
        </span>
      </div>

      <div className={styles.scoreBreakdown}>
        {breakdown.map(({ label, value, max }) => (
          <div key={label} className={styles.scoreBreakdownItem}>
            <span className={styles.scoreBreakdownLabel}>{label}</span>
            <div className={styles.scoreBreakdownBar}>
              <div className={styles.scoreTrack}>
                <div
                  className={styles.scoreFill}
                  style={{
                    width: `${(value / max) * 100}%`,
                    backgroundColor: getScoreColor((value / max) * 100),
                  }}
                />
              </div>
            </div>
            <span className={styles.scoreBreakdownValue}>
              {value}/{max}
            </span>
          </div>
        ))}
      </div>

      <div className={styles.scoreStats}>
        <div className={styles.scoreStat}>
          <div className={styles.scoreStatValue}>{patient.totalAppointments}</div>
          <div className={styles.scoreStatLabel}>Total consultas</div>
        </div>
        <div className={styles.scoreStat}>
          <div className={styles.scoreStatValue}>{patient.completedAppointments}</div>
          <div className={styles.scoreStatLabel}>Concluídas</div>
        </div>
        <div className={styles.scoreStat}>
          <div className={styles.scoreStatValue}>{patient.noShowCount}</div>
          <div className={styles.scoreStatLabel}>Faltas</div>
        </div>
      </div>
    </div>
  );
}
